/*
 * @providesModule RewardCalendarUtils
 */
import constants from 'HomeScreenConstants';
import I18n from 'INTL';
import {SKZReward, SKZRewardCalendar} from 'SKZRewardCalendar';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import {Easing} from 'react-native';

export const REWARDS_PER_ROW = 3;
export const REWARDS_PER_CALENDAR = 7;
export const ROWS = 3;

export const MS_PER_SECOND = 1000;

/* eslint-disable no-magic-numbers */
export const CELL_HEIGHT = Sizes.select({phone: 105, tablet: 180}); // from cell stylesheet
export const APPROX_CELL_WIDTH =
  (Sizes.screenWidth * Sizes.select({phone: 0.8, tablet: 0.6}) - 60) / REWARDS_PER_ROW; // headermodal width, sum of cell paddings
// These constants do not take into account outer padding around the grid
export const APPROX_GRID_HEIGHT = CELL_HEIGHT * ROWS + 10 * (ROWS - 1);
export const APPROX_GRID_WIDTH = APPROX_CELL_WIDTH * REWARDS_PER_ROW + 10 * (REWARDS_PER_ROW - 1);
// Coords where top-left corner of focused cell should be. Does not account for padding around grid
export const CENTER_X = APPROX_GRID_WIDTH / 2 - APPROX_CELL_WIDTH / 2;
export const CENTER_Y = APPROX_GRID_HEIGHT / 2 - CELL_HEIGHT / 2;

export const SCREEN_WIDTH = Sizes.screenWidth;
export const SCREEN_HEIGHT = Sizes.screenHeight;

export const REWARD_CALENDAR_CLAIM_CONSTS = {
  GRADIENT_DELAY: 300,
  GRADIENT_QUICK_DELAY: 250,
  GRADIENT_LOCATIONS: [0, 0.4, 0.8, 1],
  GRADIENT_DARK_COLORS: [
    'rgba(34, 34, 34, .9)',
    'rgba(34, 34, 34, .4)',
    'rgba(34, 34, 34, .1)',
    'transparent',
  ],
  PRIZE_CIRCLE_RADIUS: 100, //Radius of the circle of prizes around the main prize
  PRIZE_VARIATION_AMOUNT: 25, //Stagger the circle of prizes by this amount
  PRIZE_Y_OFFSET: 30, //Prize circle Y needs this offset to account for icon size.
  LABEL_END_Y: (19 * SCREEN_HEIGHT) / 32,
  PRIZES_START_X: SCREEN_WIDTH / 2,
  PRIZES_START_Y: SCREEN_HEIGHT / 3,
  IMAGE_SCALE_SMALL: 0.2,
  VERY_QUICK_DELAY: 30,
  HALF_SECOND: 500,
  FIFTH_SECOND: 200,
  PRIZE_FADE_OUT_DELAY: 580,
  PHASE_ONE_DELAY: 300,
  PHASE_TWO_DELAY: 300,
  PHASE_TWO_QUICK_DELAY: 270,
  PHASE_ONE_PRIZE_DELAY_MIN: 0,
  PHASE_ONE_PRIZE_DELAY_MAX: 100,
  PHASE_TWO_PRIZE_DELAY_MIN: 5,
  PHASE_TWO_PRIZE_DELAY_MAX: 50,
  PRIZES_FINAL_X_LEFT: Sizes.select({
    tablet: -SCREEN_WIDTH / 8, //X coordinate of Ticketz in the balance bar
    other: -SCREEN_WIDTH / 6,
  }),
  PRIZES_FINAL_X_RIGHT: Sizes.select({
    tablet: SCREEN_WIDTH / 8, //X coordinate of Cash in the balance bar
    other: -SCREEN_WIDTH / 2.5,
  }),
  PRIZES_FINAL_Y: constants.getBalanceBarHeight(),
  PRIZE_MIN_SIZE: 30, //Smallest possible prize size
  PRIZE_MAX_SIZE: 75, //Largest possible prize size
  PHASE_ONE_BEZIER: Easing.bezier(0, 0.1, 0.1, 1),
  PHASE_TWO_BEZIER: Easing.bezier(1, 0, 0.79, 1.2),
};
/* eslint-enable no-magic-numbers */

export const isUpcomingClaim = (rewardDay: number, calendar: SKZRewardCalendar): boolean => {
  const isToday = rewardDay === calendar.current_reward + 1;
  const isTomorrow = rewardDay === calendar.current_reward + 2;

  return currentRewardIsClaimed(calendar) ? isTomorrow : isToday;
};

export const isToday = (rewardDay: number, calendar: SKZRewardCalendar) => {
  return rewardDay === calendar.current_reward + 1;
};

/**
 * Determines if a reward is in valid bounds and then checks if it has been claimed
 * Used for showing the Badge icon on the Daily Reward button
 * @param calendar the reward calendar
 * @returns If the currentReward is claimed
 */
export const currentRewardIsClaimed = (calendar: SKZRewardCalendar): boolean => {
  return (
    calendar.current_reward >= 0 &&
    calendar.rewards.length > calendar.current_reward &&
    calendar.rewards[calendar.current_reward].claimed
  );
};

export const isLastReward = (calendar: SKZRewardCalendar): boolean => {
  return (
    calendar &&
    calendar.current_reward >= 0 &&
    calendar.rewards.length - 1 == calendar.current_reward
  );
};

export const getRewardSVG = (rewardType: SKZReward['type']) => {
  switch (rewardType) {
    case 'TICKETZ':
      return 'ic_ticketz_background.png';
    case 'BONUS_CASH':
    case 'CASH':
      return 'ic_cash_usd_background.png';
    case 'POINTS':
      return SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
    default:
      return 'deco_giftbox.svg';
  }
};

export const formatCurrency = (
  type: SKZReward['type'],
  value: number,
  shorten: boolean = false
) => {
  if (type === 'CASH' || type === 'BONUS_CASH') {
    return I18n.getCurrencyString(value, '$', true, true);
  } else if (type === 'TICKETZ' || type === 'POINTS') {
    if (shorten) {
      return I18n.abbreviatedNumber(value);
    } else {
      return I18n.getCurrencyString(value, '', true, true);
    }
  } else {
    return '';
  }
};

export const getRandomNumber = (min: number, max: number, allowDecimals: boolean = false) => {
  if (allowDecimals) {
    return Math.random() * (max - min) + min;
  }
  return Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1) + Math.ceil(min));
};

export const getNumberOfPrizes = (reward: SKZReward) => {
  const isCashReward = reward.type === 'BONUS_CASH' || reward.type === 'CASH';
  const PRIZE_SIZE_100 = 100;
  const PRIZE_SIZE_200 = 200;
  const PRIZE_SIZE_300 = 300;
  const DEFAULT_PRIZES = 15;
  const PRIZES_OVER_100_UNDER_300 = 25;
  const PRIZES_OVER_300 = 35;
  let prizeAmount = reward.value.amount;

  if (isCashReward) {
    prizeAmount *= PRIZE_SIZE_100;
  }

  if (prizeAmount >= PRIZE_SIZE_300) {
    //This logic linearly chooses a number between 15 and 35.
    const prizeAfterHundredCalc =
      PRIZES_OVER_100_UNDER_300 + 1 * ((prizeAmount - PRIZE_SIZE_100) / PRIZE_SIZE_200) >
      PRIZES_OVER_300
        ? PRIZES_OVER_300
        : Math.floor(
            PRIZES_OVER_100_UNDER_300 + 1 * ((prizeAmount - PRIZE_SIZE_100) / PRIZE_SIZE_200)
          );
    return prizeAfterHundredCalc;
  } else if (prizeAmount >= PRIZE_SIZE_100) {
    return PRIZES_OVER_100_UNDER_300;
  } else {
    return DEFAULT_PRIZES;
  }
};
